import {useRef} from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2 } from 'lucide-react';

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    className?: string;
    minHeight?: string;
}

export function RichTextEditor({
    value,
    onChange,
    label,
    placeholder,
    className = '',
    minHeight = '120px'
}: RichTextEditorProps) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const insertFormat = (prefix: string, suffix: string = '') => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const before = text.substring(0, start);
        const selection = text.substring(start, end);
        const after = text.substring(end);

        const newValue = `${before}${prefix}${selection}${suffix}${after}`;
        onChange(newValue);

        // Restore selection / cursor
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(
                start + prefix.length,
                end + prefix.length
            );
        }, 0);
    };

    const handleBold = () => insertFormat('**', '**');
    const handleItalic = () => insertFormat('*', '*');
    const handleUnderline = () => insertFormat('<u>', '</u>');
    const handleH1 = () => insertFormat('\n# ');
    const handleH2 = () => insertFormat('\n## ');
    const handleList = () => insertFormat('\n- ');
    const handleOrderedList = () => insertFormat('\n1. ');

    return (
        <div className={className}>
            {label && <label className="label">{label}</label>}
            <div className="border border-gray-300 rounded-md overflow-hidden bg-white dark:bg-slate-900 dark:border-slate-700 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500">
                <div className="flex items-center gap-1 p-2 bg-gray-50 border-b border-gray-200 overflow-x-auto dark:bg-slate-800 dark:border-slate-700">
                    {/* Text Style */}
                    <div className="flex items-center gap-0.5">
                        <button type="button" onClick={handleBold} className="p-1.5 rounded hover:bg-gray-200 text-gray-700 dark:text-slate-200 dark:hover:bg-slate-700" title="Bold">
                            <Bold size={16} />
                        </button>
                        <button type="button" onClick={handleItalic} className="p-1.5 rounded hover:bg-gray-200 text-gray-700 dark:text-slate-200 dark:hover:bg-slate-700" title="Italic">
                            <Italic size={16} />
                        </button>
                        <button type="button" onClick={handleUnderline} className="p-1.5 rounded hover:bg-gray-200 text-gray-700 dark:text-slate-200 dark:hover:bg-slate-700" title="Underline">
                            <Underline size={16} />
                        </button>
                    </div>

                    <div className="w-px h-5 bg-gray-300 mx-1.5 dark:bg-slate-600" />

                    {/* Headings / Size */}
                    <div className="flex items-center gap-0.5">
                        <button type="button" onClick={handleH1} className="p-1.5 rounded hover:bg-gray-200 text-gray-700 dark:text-slate-200 dark:hover:bg-slate-700" title="Large Heading">
                            <Heading1 size={16} />
                        </button>
                        <button type="button" onClick={handleH2} className="p-1.5 rounded hover:bg-gray-200 text-gray-700 dark:text-slate-200 dark:hover:bg-slate-700" title="Medium Heading">
                            <Heading2 size={16} />
                        </button>
                    </div>

                    <div className="w-px h-5 bg-gray-300 mx-1.5 dark:bg-slate-600" />

                    {/* Lists */}
                    <div className="flex items-center gap-0.5">
                        <button type="button" onClick={handleList} className="p-1.5 rounded hover:bg-gray-200 text-gray-700 dark:text-slate-200 dark:hover:bg-slate-700" title="Bullet List">
                            <List size={16} />
                        </button>
                        <button type="button" onClick={handleOrderedList} className="p-1.5 rounded hover:bg-gray-200 text-gray-700 dark:text-slate-200 dark:hover:bg-slate-700" title="Numbered List">
                            <ListOrdered size={16} />
                        </button>
                    </div>
                </div>
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full p-3 focus:outline-none resize-y text-sm font-sans bg-white text-gray-900 placeholder-gray-500 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-400"
                    style={{ minHeight: minHeight }}
                    placeholder={placeholder}
                />
            </div>
            <p className="text-xs text-gray-400 mt-1 dark:text-slate-400">
                Markdown supported: **bold**, *italic*, # Heading, <u>Underline</u>
            </p>
        </div>
    );
}
